<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import QuotesAPI from 'dashboard/api/talkviaQuotes';
import ContactsAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';
import QuickQuoteDialog from '../conversation/QuickQuoteDialog.vue';
import { money } from './financePresentation';

const emit = defineEmits(['convert']);
const route = useRoute();
const quotes = ref([]);
const filters = reactive({
  status: '',
  q: '',
  delivery_start: '',
  delivery_end: '',
  pickup_start: '',
  pickup_end: '',
});
const showStatusFilter = ref(false);
const activeFilters = ref(false);
let loadRequest = 0;
const loading = ref(false);
const failed = ref(false);
const busy = ref(false);
const dialog = ref();
const quoteDialog = ref();
const scheduleDialog = ref();
const selectedContact = ref(null);
const contacts = ref([]);
const selectedContactId = ref('');
const scheduleQuote = ref(null);
const startsAt = ref('');
const contactOptions = computed(() =>
  contacts.value.map(c => ({
    value: c.id,
    label: `${c.name} · ${c.phone_number || c.email || ''}`,
  }))
);
const labels = {
  draft: 'Rascunho',
  sent: 'Enviado',
  approved: 'Aprovado',
  expired: 'Expirado',
  converted: 'Contrato gerado',
};
const statusFilterLabel = computed(() => labels[filters.status] || 'Todos');
const statusFilterItems = computed(() =>
  [
    { value: '', label: 'Todos' },
    ...Object.entries(labels).map(([value, label]) => ({ value, label })),
  ].map(option => ({
    ...option,
    action: 'status_filter',
    icon: option.value === filters.status ? 'i-hugeicons-tick-02' : '',
    isSelected: option.value === filters.status,
  }))
);
const selectStatus = ({ value }) => {
  filters.status = value;
  showStatusFilter.value = false;
};
const dateTime = value =>
  value
    ? new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    : '—';
const available = quote =>
  quote.status !== 'expired' &&
  !quote.contract_id &&
  new Date(quote.expires_at) > new Date();

const load = async () => {
  loadRequest += 1;
  const request = loadRequest;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await QuotesAPI.get({ ...filters });
    if (request !== loadRequest) return;
    quotes.value = data.quotes;
    activeFilters.value = Object.values(filters).some(Boolean);
  } catch (error) {
    if (request !== loadRequest) return;
    failed.value = true;
    useAlert(
      error?.response?.data?.error || 'Não foi possível carregar os orçamentos.'
    );
  } finally {
    if (request === loadRequest) loading.value = false;
  }
};
const clearFilters = () => {
  showStatusFilter.value = false;
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
  load();
};
let searchRequest = 0;
const searchContacts = async (q = '') => {
  searchRequest += 1;
  const request = searchRequest;
  try {
    const { data } = q ? await ContactsAPI.search(q) : await ContactsAPI.get(1);
    if (request === searchRequest) contacts.value = data.payload || [];
  } catch {
    useAlert('Não foi possível buscar os contatos.');
  }
};
const openCreate = () => {
  selectedContactId.value = '';
  dialog.value?.open();
  searchContacts();
};
const createForContact = async () => {
  selectedContact.value = contacts.value.find(
    c => Number(c.id) === Number(selectedContactId.value)
  );
  if (!selectedContact.value) return;
  dialog.value?.close();
  await nextTick();
  quoteDialog.value?.open();
};
const edit = async quote => {
  if (busy.value) return;
  busy.value = true;
  try {
    const { data } = await ContactsAPI.show(quote.contact_id);
    selectedContact.value = data.payload || data;
    await nextTick();
    quoteDialog.value?.open(quote);
  } catch {
    useAlert('Não foi possível abrir o contato deste orçamento.');
  } finally {
    busy.value = false;
  }
};
const approve = async quote => {
  if (busy.value) return;
  busy.value = true;
  try {
    const { data } = await QuotesAPI.approve(quote.id);
    await load();
    useAlert(
      data?.calendar_event_id
        ? 'Aprovação registrada e orçamento incluído na agenda.'
        : 'Aprovação do orçamento registrada.',
      'success'
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível aprovar o orçamento.'
    );
  } finally {
    busy.value = false;
  }
};
const openSchedule = quote => {
  scheduleQuote.value = quote;
  startsAt.value = quote.payload.scheduled_at || '';
  scheduleDialog.value?.open();
};
const saveSchedule = async () => {
  if (busy.value || !startsAt.value) return;
  busy.value = true;
  try {
    await QuotesAPI.schedule(scheduleQuote.value.id, startsAt.value);
    scheduleDialog.value?.close();
    await load();
    useAlert('Orçamento cadastrado na agenda.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível cadastrar na agenda.'
    );
  } finally {
    busy.value = false;
  }
};
const openFromRoute = async () => {
  if (!route.query.edit_quote_id) return;
  try {
    const { data } = await QuotesAPI.show(route.query.edit_quote_id);
    if (!available(data)) {
      useAlert('Este orçamento está expirado ou convertido.');
      return;
    }
    await edit(data);
  } catch {
    useAlert(
      'Orçamento indisponível. Ele pode ter sido excluído após a validade.'
    );
  }
};
onMounted(async () => {
  await load();
  await openFromRoute();
});
watch(() => route.query.edit_quote_id, openFromRoute);
defineExpose({ openCreate, load });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="border border-n-weak bg-n-solid-2 p-4">
    <div class="mb-4">
      <h2 class="mb-1 text-base font-semibold">Orçamentos</h2>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ quotes.length }} orçamento(s) · Validade padrão de 24 horas. Exclusão
        24 horas após expirar.
      </p>
    </div>
    <form class="mb-4 space-y-3" @submit.prevent="load">
      <div class="grid items-end gap-3 md:grid-cols-[10rem_1fr_auto_auto]">
        <div class="flex flex-col gap-2 text-sm font-medium">
          <span>Status</span>
          <div
            v-on-click-outside="() => (showStatusFilter = false)"
            class="relative"
          >
            <Button
              type="button"
              color="slate"
              variant="faded"
              size="md"
              trailing-icon
              icon="i-hugeicons-arrow-down-01"
              class="!h-10 w-full !justify-between !rounded-lg !border !border-n-weak !bg-n-solid-1 !px-3 !text-n-slate-12 shadow-sm"
              :label="statusFilterLabel"
              :aria-expanded="showStatusFilter"
              aria-label="Filtrar por status do orçamento"
              :disabled="loading"
              data-test="quote-status"
              @click="showStatusFilter = !showStatusFilter"
            />
            <DropdownMenu
              v-if="showStatusFilter"
              :menu-items="statusFilterItems"
              class="left-0 top-11 w-full text-left"
              @action="selectStatus"
            />
          </div>
        </div>
        <label class="flex flex-col gap-2 text-sm font-medium">
          Buscar cliente
          <input
            v-model="filters.q"
            data-test="quote-search"
            class="mb-0 h-10 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            placeholder="Nome, telefone ou CPF/CNPJ"
          />
        </label>
        <Button
          type="submit"
          label="Filtrar"
          icon="i-lucide-search"
          :disabled="loading"
        />
        <Button
          type="button"
          label="Limpar filtros"
          variant="faded"
          :disabled="loading"
          @click="clearFilters"
        />
      </div>
      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <DateField
          v-model="filters.delivery_start"
          label="Entrega de"
          :max="filters.delivery_end"
          data-test="delivery-start"
        />
        <DateField
          v-model="filters.delivery_end"
          label="Entrega até"
          :min="filters.delivery_start"
          data-test="delivery-end"
        />
        <DateField
          v-model="filters.pickup_start"
          label="Retirada de"
          :max="filters.pickup_end"
          data-test="pickup-start"
        />
        <DateField
          v-model="filters.pickup_end"
          label="Retirada até"
          :min="filters.pickup_start"
          data-test="pickup-end"
        />
      </div>
    </form>
    <p v-if="loading" role="status" class="text-sm text-n-slate-11">
      Carregando orçamentos…
    </p>
    <Button
      v-else-if="failed"
      label="Tentar novamente"
      size="sm"
      @click="load"
    />
    <div v-else class="overflow-x-auto">
      <table class="w-full min-w-[960px] text-left text-sm">
        <thead>
          <tr class="border-b border-n-weak text-xs uppercase text-n-slate-10">
            <th scope="col" class="px-2 py-3 font-semibold">Cliente</th>
            <th scope="col" class="px-2 py-3 text-right font-semibold">
              Valor
            </th>
            <th scope="col" class="px-2 py-3 font-semibold">
              Entrega / retirada
            </th>
            <th scope="col" class="px-2 py-3 font-semibold">Validade</th>
            <th scope="col" class="px-2 py-3 font-semibold">Status</th>
            <th scope="col" class="px-2 py-3 text-right font-semibold">
              Ações
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!quotes.length">
            <td colspan="6" class="px-2 py-8 text-center text-n-slate-11">
              {{
                activeFilters
                  ? 'Nenhum orçamento encontrado para os filtros selecionados.'
                  : 'Nenhum orçamento cadastrado.'
              }}
            </td>
          </tr>
          <tr
            v-for="quote in quotes"
            :key="quote.id"
            class="border-b border-n-weak last:border-0"
            data-test="quote-row"
          >
            <td class="px-2 py-4 align-top">
              <p class="mb-1 font-medium text-n-slate-12">
                {{
                  quote.payload.recipient_name ||
                  quote.contact?.name ||
                  'Cliente'
                }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                #{{ quote.id }} ·
                {{
                  quote.payload.recipient_phone ||
                  quote.contact?.phone_number ||
                  'Sem telefone'
                }}
              </p>
            </td>
            <td
              class="whitespace-nowrap px-2 py-4 text-right align-top font-semibold tabular-nums"
            >
              {{ money(quote.invoice_total) }}
            </td>
            <td class="px-2 py-4 align-top text-xs text-n-slate-11">
              <p class="mb-1">
                Entrega: {{ dateTime(quote.payload.scheduled_at) }}
              </p>
              <p class="mb-0">
                Retirada: {{ dateTime(quote.payload.pickup_at) }}
              </p>
            </td>
            <td
              class="px-2 py-4 align-top text-xs tabular-nums text-n-slate-11"
            >
              {{ dateTime(quote.expires_at) }}
            </td>
            <td class="px-2 py-4 align-top">
              <span
                class="inline-flex whitespace-nowrap rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11"
                >{{ labels[quote.status] }}</span
              >
              <p
                v-if="quote.calendar_event_id"
                class="mb-0 mt-2 text-xs text-n-slate-11"
              >
                Na agenda
              </p>
            </td>
            <td class="max-w-72 px-2 py-4 align-top">
              <div
                v-if="available(quote)"
                class="flex flex-wrap justify-end gap-2"
              >
                <Button
                  label="Editar"
                  size="sm"
                  variant="faded"
                  :disabled="busy"
                  @click="edit(quote)"
                />
                <Button
                  v-if="quote.status === 'sent'"
                  label="Registrar aprovação"
                  size="sm"
                  variant="faded"
                  :disabled="busy"
                  @click="approve(quote)"
                />
                <Button
                  v-if="quote.status === 'approved' && !quote.calendar_event_id"
                  label="Cadastrar na agenda"
                  size="sm"
                  variant="faded"
                  :disabled="busy"
                  @click="openSchedule(quote)"
                />
                <Button
                  label="Criar contrato"
                  size="sm"
                  :disabled="busy"
                  @click="emit('convert', quote)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
  <Dialog
    ref="dialog"
    title="Criar orçamento"
    description="Selecione o cliente do orçamento."
  >
    <ComboBox
      v-model="selectedContactId"
      :options="contactOptions"
      placeholder="Buscar contato"
      @search="searchContacts"
    />
    <template #footer>
      <Button
        label="Continuar"
        :disabled="!selectedContactId"
        @click="createForContact"
      />
    </template>
  </Dialog>
  <QuickQuoteDialog
    ref="quoteDialog"
    :contact="selectedContact || {}"
    @sent="load"
    @saved="load"
  />
  <Dialog
    ref="scheduleDialog"
    title="Cadastrar orçamento na agenda"
    description="Confirme o horário do compromisso."
  >
    <DateField v-model="startsAt" label="Data e hora" with-time />
    <template #footer>
      <Button
        label="Cadastrar na agenda"
        :disabled="!startsAt || busy"
        :is-loading="busy"
        @click="saveSchedule"
      />
    </template>
  </Dialog>
</template>
